/**
 * Offline-first storage utility
 * Integrates generated media files with the new offline sync system
 */

// @ts-ignore - electron API is injected at runtime
declare const window: Window & {
  api?: {
    saveFile: (
      sessionId: string,
      fileName: string,
      fileType: 'photos' | 'videos' | 'gifs',
      data: ArrayBuffer,
      metadata?: {
        width?: number
        height?: number
        duration?: number
        frameCount?: number
        mimeType?: string
        fileHash?: string
      }
    ) => Promise<{ success: boolean; path?: string; resourceId?: string; error?: string }>
  }
}

interface SaveMediaOptions {
  sessionId: string
  fileName: string
  file: File | Blob
  metadata?: {
    width?: number
    height?: number
    duration?: number
    frameCount?: number
    mimeType?: string
    fileHash?: string
    alreadyUploaded?: boolean
  }
  fileType: 'photos' | 'videos' | 'gifs'
}

/**
 * Save media file to offline storage
 * Automatically:
 * - Saves file to local disk
 * - Saves metadata to SQLite
 * - Adds to upload queue
 * - Returns file path and resource ID
 */
export async function saveMediaOffline(options: SaveMediaOptions): Promise<{
  success: boolean
  skipped?: boolean
  path?: string
  resourceId?: string
  error?: string
}> {
  try {
    const { sessionId, fileName, file, metadata, fileType } = options

    // Convert File/Blob to ArrayBuffer for IPC
    const arrayBuffer = await file.arrayBuffer()

    // Save via IPC to main process (main process will convert to Buffer)
    if (!window.api || typeof window.api.saveFile !== 'function') {
      console.debug('[offlineStorage] IPC saveFile API not available; skipping offline save.')
      return {
        success: false,
        skipped: true,
        error: 'Offline storage unavailable in this runtime'
      }
    }

    // @ts-ignore - saveFile exists on window.api at runtime
    const result = await window.api.saveFile(sessionId, fileName, fileType, arrayBuffer, metadata)

    return result
  } catch (error) {
    if (error instanceof Error && error.message.includes('IPC API not available')) {
      console.debug('[offlineStorage] IPC save failed:', error.message)
      return {
        success: false,
        skipped: true,
        error: error.message
      }
    }
    console.error('Failed to save media offline:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Convert File/Blob to Buffer
 */
export function fileToBuffer(file: File | Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (): void => {
      const arrayBuffer = reader.result as ArrayBuffer
      resolve(arrayBuffer)
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export default {
  saveMediaOffline,
  fileToBuffer
}
